import React, { useEffect, useState } from "react";
import { useLang } from "../LangContext";
import { useTenant } from "../TenantContext";
import { HashLink } from "../HashLink";
import { DIGITAL_TWIN_IMAGE } from "../data";
import { ArrowLeft, Move3d, MapPin, Sparkles, Play } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GemelloDigitalePage() {
    const { t, lang } = useLang();
    const { slug } = useTenant();
    const [cfg, setCfg] = useState({ enabled: false, space_id: "", mpskin_url: "", title: "", description: "", thumbnail: "" });
    const [started, setStarted] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0 });
        axios.get(`${API}/settings/borgo-digital-twin`, { params: { borgo: slug } }).then((r) => setCfg(r.data)).catch(() => {});
    }, [slug]);

    // Resolve viewer URL: MPSKIN takes priority if provided, else Matterport
    const mpskinUrl = cfg.mpskin_url?.trim();
    const spaceId = cfg.space_id?.trim();
    const matterportEmbed = spaceId ? `https://my.matterport.com/show/?m=${encodeURIComponent(spaceId)}&play=1&qs=1&brand=0` : "";
    const viewerUrl = mpskinUrl || matterportEmbed;
    // Auto-show the tour whenever a viewer URL is configured. The legacy "enabled" flag
    // referred to the ADMIN internal Dashboard 3D, not to the public tour visibility.
    // Requiring both was preventing tours from showing (e.g. Dozza with MPSKIN URL set
    // but "Abilita Vista 3D" unchecked).
    const hasTour = !!viewerUrl;

    return (
        <div className="pt-24 min-h-screen bg-stone-900 text-white" data-testid="gemello-page">
            <section
                className="relative min-h-[45vh] md:min-h-[55vh] flex items-center justify-center overflow-hidden"
                style={{
                    backgroundImage: `url(${cfg.thumbnail || DIGITAL_TWIN_IMAGE})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(6,78,59,0.55) 0%, rgba(6,78,59,0.85) 100%)" }} />
                <div className="relative z-10 text-center px-6 py-20 max-w-4xl">
                    <div className="text-xs font-semibold tracking-[0.3em] uppercase text-emerald-300 mb-4">
                        3D · INTERACTIVE TOUR
                    </div>
                    <h1 className="font-serif text-5xl md:text-7xl font-semibold text-white leading-tight">
                        {cfg.title || t.gemello_title}
                    </h1>
                    <p className="mt-6 text-lg text-emerald-50/90 max-w-2xl mx-auto">
                        {cfg.description || t.gemello_sub}
                    </p>
                </div>
            </section>

            <section className="py-16 px-6 lg:px-10" style={{ background: "linear-gradient(180deg, #064e3b 0%, #0c3428 100%)" }}>
                <div className="max-w-6xl mx-auto">
                    {hasTour ? (
                        <div className="rounded-3xl overflow-hidden border border-emerald-400/20 bg-black shadow-2xl" data-testid="borgo-tour-embed">
                            {started ? (
                                <div className="relative aspect-video">
                                    <iframe
                                        src={viewerUrl}
                                        title="Gemello Digitale Tadasuni"
                                        className="w-full h-full"
                                        allow="fullscreen; xr-spatial-tracking; vr; camera; microphone; accelerometer; gyroscope"
                                        allowFullScreen
                                        frameBorder="0"
                                    />
                                    <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 rounded-full bg-black/50 backdrop-blur text-xs uppercase tracking-wider text-emerald-200 font-bold">
                                        {mpskinUrl ? "MPSKIN" : "Matterport"}
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setStarted(true)}
                                    className="relative w-full aspect-video group"
                                    data-testid="borgo-tour-start"
                                    aria-label="Avvia tour"
                                >
                                    <img src={cfg.thumbnail || DIGITAL_TWIN_IMAGE} alt="Tour preview" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
                                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                                        <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center pulse-ring group-hover:scale-110 transition-transform">
                                            <Play size={28} className="text-emerald-700 ml-1" />
                                        </div>
                                        <div className="font-serif text-2xl text-white drop-shadow-lg">
                                            {lang === "it" ? "Avvia Tour Virtuale" : "Start Virtual Tour"}
                                        </div>
                                    </div>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="rounded-3xl border border-emerald-400/20 bg-emerald-950/50 aspect-video overflow-hidden relative">
                            <img src={cfg.thumbnail || DIGITAL_TWIN_IMAGE} alt="Tadasuni 3D" className="w-full h-full object-cover opacity-60" />
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                                <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center pulse-ring">
                                    <Move3d size={32} className="text-emerald-700" />
                                </div>
                                <div className="text-center px-4">
                                    <div className="font-serif text-3xl font-semibold text-white mb-2">
                                        {lang === "it" ? "Tour Virtuale in arrivo" : "Virtual Tour coming soon"}
                                    </div>
                                    <p className="text-emerald-100/80 text-sm max-w-md">
                                        {lang === "it"
                                            ? "Il gemello digitale interattivo di Tadasuni sarà presto disponibile."
                                            : "The interactive digital twin of Tadasuni will be available soon."}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                        {[
                            { icon: Move3d, title: lang === "it" ? "Navigazione 3D" : "3D Navigation", desc: lang === "it" ? "Cammina virtualmente per le vie del borgo." : "Walk virtually through the streets." },
                            { icon: MapPin, title: lang === "it" ? "Punti di interesse" : "Points of Interest", desc: lang === "it" ? "Chiese, piazze e panorami cliccabili." : "Churches, squares and clickable views." },
                            { icon: Sparkles, title: lang === "it" ? "Riprese aeree" : "Aerial views", desc: lang === "it" ? "Vista mozzafiato sul Lago Omodeo." : "Breathtaking views on Lake Omodeo." },
                        ].map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <div key={i} className="rounded-2xl border border-emerald-400/20 bg-emerald-950/50 p-6" data-testid={`gemello-feature-${i}`}>
                                    <div className="w-11 h-11 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                                        <Icon size={20} className="text-emerald-300" />
                                    </div>
                                    <h3 className="font-serif text-xl font-semibold text-white mb-2">{f.title}</h3>
                                    <p className="text-emerald-100/70 text-sm">{f.desc}</p>
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-center mt-14">
                        <HashLink
                            to="/"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-emerald-800 font-semibold hover:bg-emerald-50 hover:-translate-y-0.5 transition-all shadow-lg"
                            data-testid="back-home-btn-gemello"
                        >
                            <ArrowLeft size={18} />
                            {t.back_home}
                        </HashLink>
                    </div>
                </div>
            </section>
        </div>
    );
}
